import { createSelector } from "reselect";
import { createReducer } from "typesafe-actions";

import DefaultActivityTypeName from "@mapmycustomers/shared/enum/activity/DefaultActivityTypeName";
import { Activity } from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";

import { RootState } from "../rootReducer";

import {
  Actions,
  bulkUpdateActivityType,
  createActivityType,
  deleteActivityType,
  fetchActivityTypes,
  fetchCoachingTaskActivityType,
  updateActivityType,
} from "./actions";

export interface ActivityState {
  activitiesOfActivityType: Record<ActivityType["id"], Activity[]>;
  activityTypeDeletingIds: ActivityType["id"][];
  activityTypeFetchingActivitiesIds: ActivityType["id"][];
  activityTypes: ActivityType[];
  activityTypeUpdatingIds: ActivityType["id"][];
  coachingTaskActivityType?: ActivityType;
  coachingTaskActivityTypeRequested: boolean;
  isActivityTypeCreating: boolean;
  isBulkActivityTypeUpdating: boolean;
}

const initialState: ActivityState = {
  activitiesOfActivityType: [],
  activityTypeDeletingIds: [],
  activityTypeFetchingActivitiesIds: [],
  activityTypes: [],
  activityTypeUpdatingIds: [],
  coachingTaskActivityType: undefined,
  coachingTaskActivityTypeRequested: false,
  isActivityTypeCreating: false,
  isBulkActivityTypeUpdating: false,
};

const activity = createReducer<ActivityState, Actions>(initialState)
  .handleAction(fetchActivityTypes.success, (state, { payload }) => ({
    ...state,
    activityTypes: payload,
  }))
  // Latched on .request by design — retry on failure is the saga's job, not DashboardPage's.
  .handleAction(fetchCoachingTaskActivityType.request, (state) => ({
    ...state,
    coachingTaskActivityTypeRequested: true,
  }))
  .handleAction(fetchCoachingTaskActivityType.success, (state, { payload }) => ({
    ...state,
    coachingTaskActivityType: payload,
  }))
  .handleAction(createActivityType.request, (state) => ({
    ...state,
    isActivityTypeCreating: true,
  }))
  .handleAction(createActivityType.success, (state, { payload }) => ({
    ...state,
    activityTypes: state.activityTypes.concat(payload),
    isActivityTypeCreating: false,
  }))
  .handleAction(createActivityType.failure, (state) => ({
    ...state,
    isActivityTypeCreating: false,
  }))
  .handleAction(updateActivityType.request, (state, { payload }) => ({
    ...state,
    activityTypeUpdatingIds: state.activityTypeUpdatingIds.concat(payload.id),
  }))
  .handleAction(updateActivityType.success, (state, { payload }) => ({
    ...state,
    activityTypes: state.activityTypes.map((activityType) => {
      if (activityType.id === payload.id) {
        return {
          ...activityType,
          ...payload,
        };
      }

      return activityType;
    }),
    activityTypeUpdatingIds: state.activityTypeUpdatingIds.filter((id) => id !== payload.id),
  }))
  .handleAction(updateActivityType.failure, (state, { payload }) => ({
    ...state,
    activityTypeUpdatingIds: state.activityTypeUpdatingIds.filter((id) => id !== payload),
  }))
  .handleAction(deleteActivityType.request, (state, { payload }) => ({
    ...state,
    activityTypeDeletingIds: state.activityTypeDeletingIds.concat(payload),
  }))
  .handleAction(deleteActivityType.success, (state, { payload }) => ({
    ...state,
    activityTypeDeletingIds: state.activityTypeDeletingIds.filter((id) => id !== payload.id),
    activityTypes: state.activityTypes.map((activityType) =>
      activityType.id === payload.id
        ? { ...activityType, deletedAt: payload.deletedAt }
        : activityType
    ),
  }))
  .handleAction(deleteActivityType.failure, (state, { payload }) => ({
    ...state,
    activityTypeDeletingIds: state.activityTypeDeletingIds.filter((id) => id !== payload),
  }))
  .handleAction(bulkUpdateActivityType.request, (state) => ({
    ...state,
    isBulkActivityTypeUpdating: true,
  }))
  .handleAction(bulkUpdateActivityType.success, (state, { payload }) => ({
    ...state,
    activityTypes: state.activityTypes.map((activityType) => {
      const updatedActivityType = payload.find((at) => at.id === activityType.id);
      if (updatedActivityType) {
        return {
          ...activityType,
          ...updatedActivityType,
        };
      }

      return activityType;
    }),
    isBulkActivityTypeUpdating: false,
  }))
  .handleAction(bulkUpdateActivityType.failure, (state) => ({
    ...state,
    isBulkActivityTypeUpdating: false,
  }));

const activityState = (state: RootState) => state.activity;

export const getActivitiesOfActivityType = createSelector(
  activityState,
  ({ activitiesOfActivityType }) => activitiesOfActivityType
);

export const getAllActivityTypes = createSelector(
  activityState,
  ({ activityTypes }) => activityTypes
);

export const getActivityTypes = createSelector(activityState, ({ activityTypes }) =>
  activityTypes.filter(({ deletedAt }) => !deletedAt)
);

export const getActiveActivityTypes = createSelector(getActivityTypes, (activityTypes) =>
  activityTypes.filter((activityType) => activityType.active)
);

export const getDefaultActivityTypes = createSelector(getActivityTypes, (activityTypes) =>
  activityTypes.filter((activityType) => activityType.isDefault)
);

export const getCheckInActivityTypes = createSelector(getActivityTypes, (activityTypes) =>
  activityTypes.filter((activityType) => activityType.checkInEnabled)
);

export const getEmailActivityType = createSelector(
  getActivityTypes,
  (activityTypes) =>
    activityTypes.find((activityType) => activityType.name === DefaultActivityTypeName.EMAIL)!
);

export const getCoachingTaskActivityType = createSelector(
  activityState,
  ({ coachingTaskActivityType }) => coachingTaskActivityType
);

export const isCoachingTaskActivityTypeRequested = createSelector(
  activityState,
  ({ coachingTaskActivityTypeRequested }) => coachingTaskActivityTypeRequested
);

export const getIsActivityTypeCreating = createSelector(
  activityState,
  ({ isActivityTypeCreating }) => isActivityTypeCreating
);
export const getActivityTypeDeletingIds = createSelector(
  activityState,
  ({ activityTypeDeletingIds }) => activityTypeDeletingIds
);
export const getActivityTypeFetchingActivitiesIds = createSelector(
  activityState,
  ({ activityTypeFetchingActivitiesIds }) => activityTypeFetchingActivitiesIds
);
export const getActivityTypeUpdatingIds = createSelector(
  activityState,
  ({ activityTypeUpdatingIds }) => activityTypeUpdatingIds
);
export const getIsBulkActivityTypeUpdating = createSelector(
  activityState,
  ({ isBulkActivityTypeUpdating }) => isBulkActivityTypeUpdating
);

export type ActivityActions = Actions;
export default activity;
